import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Foglio che sale dal basso: il contenitore di filtri, azioni e menu sul
 * telefono (redesign mobile 2026-10-23, Pixel 9).
 *
 * In portale su `document.body`: dentro la colonna della pagina un antenato con
 * `overflow` o `transform` lo taglierebbe. Escape e tocco sullo sfondo chiudono,
 * lo scroll della pagina sotto resta fermo finche' e' aperto. Il pollice arriva
 * in fondo allo schermo, non in cima: per questo i tasti del foglio stanno nel
 * `footer`, appoggiati al bordo basso.
 */
export function BottomSheet({
  open,
  onClose,
  title,
  children,
  footer,
  testId,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Tasti fissi in fondo al foglio (non scorrono col contenuto). */
  footer?: ReactNode;
  testId?: string;
}) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  // onClose cambia a ogni render del genitore: col ref l'effetto non si
  // riattacca (e non ruba il focus) a ogni tick di polling della pagina.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = window.setTimeout(() => panelRef.current?.focus(), 0);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(t);
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[45]" data-testid={testId}>
      <button
        type="button"
        aria-label="Chiudi"
        onClick={onClose}
        className="om-fade absolute inset-0 cursor-default bg-ink/40"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="om-sheet absolute inset-x-0 bottom-0 flex max-h-[88dvh] flex-col rounded-t-[22px] bg-paper shadow-[0_-12px_40px_rgba(31,27,22,0.18)] outline-none"
      >
        <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-line-2" aria-hidden="true" />
        <header className="flex shrink-0 items-center justify-between gap-3 px-5 pb-2 pt-3">
          <h2
            id={titleId}
            className="font-display text-[20px] font-semibold tracking-[-0.01em] text-ink"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="-mr-2 grid h-11 w-11 place-items-center rounded-full text-ink active:bg-paper-2"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-4">
          {children}
        </div>
        {footer && (
          <div className="shrink-0 border-t border-line bg-card px-5 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3">
            {footer}
          </div>
        )}
        {!footer && <div className="h-[env(safe-area-inset-bottom)] shrink-0" aria-hidden="true" />}
      </div>
    </div>,
    document.body,
  );
}
